import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import 'react-multi-carousel/lib/styles.css';
import Select from 'react-select';
import Accordion from 'react-bootstrap/Accordion';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LateralPopUp from "../LateralPopUp";
import Header from "../Header";
import ContactConcierge from "../ContactConcierge";
import { HashLink } from "react-router-hash-link";
import { Calendar } from "primereact/calendar";
import CardsConcierge from "../CardsConcierge";
import CardsExperiences from "../../utilities/CardsExperiences.js";
import { GetAvailabilityById, CheckMinNights, FormatDate } from "../../utils/functions.js";
import { useLocation } from 'react-router-dom';
import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import Seo from "../Seo.js";
import { IoHomeOutline } from "react-icons/io5";
import { Modal } from "@mui/base";
import PropertyRules from "../../pages/PropertyRules.js";
import MasonryProperty from "../MasonryProperty.js";

const TemplatePropertyDetail = ({ villa }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { checkIn, checkOut } = location.state || {}
    const [checkInDate, setCheckInDate] = useState(checkIn ? checkIn : null);
    const [checkOutDate, setCheckOutDate] = useState(checkOut ? checkOut : null);
    const [guests, setGuests] = useState(null);
    const overviewRef = useRef(null);
    const finalRef = useRef(null);
    const [isBookingVisible, setIsBookingVisible] = useState(false);
    const [dateLocked, setDateLocked] = useState(null);


    const [checkoutMinDate, setCheckoutMinDate] = useState(null);
    const [checkoutMaxDate, setCheckoutMaxDate] = useState(null);
    const [checkoutDisabledDates, setCheckoutDisabledDates] = useState([]); 
    const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(true);    
    
    const [minNights, setMinNights] = useState();
    const [priceNight, setPriceNight] = useState(null);
    const [priceTotal, setPriceTotal] = useState(null);
    const [canCheck, setCanCheck] = useState(false);
    const isMobileTablet = window.matchMedia("screen and (max-width: 991px)").matches;
    const [isPopUpVisible, setIsPopUpVisible] = useState(true);
    const [isClosed, setIsClosed] = useState(false);
    const [isOpenPropertyRules, setIsOpenPropertyRules] = useState(false);
    const [isModalMasonryOpen, setIsModalMasonryOpen] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const [latitude, setLatitude] = useState(0);
    const [longitude, setLongitude] = useState(0);
    const [paymentModeVisible, setPaymentModeVisible] = useState(false);
    const [link, setLink] = useState(null);
    const bookNowButtonRef = useRef(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleScroll = () => {
            if (overviewRef.current && window.scrollY > overviewRef.current.offsetTop && window.scrollY < finalRef.current.offsetTop) {
                setIsBookingVisible(true);
            } else {
                setIsBookingVisible(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const addDays = (date, days) => {
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    };

    const accordionItems = [
        { title: t("DetailedVillaDescription"), description: villa.villa.description, id: "overview" },
        { title: t('Rooms'), description: villa.rooms, id: "rooms" },
        { title: t("ArrivalAndDeparture"), description: villa.arrivalsAndDeparture, id: "arrivalDeparture" },
        { title: t("FeaturesAmenities"), description: villa.amenities, id: "amenities" },
        { title: t("TouristTax"), description: villa.touristsTax, id: "touristTax" },
        { title: t("ServiceAdditionalCharge"), description: villa.servicesWithAdditionalCharge, id: "servicesWithAdditionalCharge" },
    ];

    const guestOptions = Array.from(
        { length: villa?.guests ?? 0 },
        (_, i) => ({ value: i + 1, label: (i + 1).toString() })
    );




    const normalizedGlobalDateLocked = useMemo(() => {
        if (!dateLocked) return [];
        return [...new Set(dateLocked.map(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()))]
               .map(t => new Date(t));
    }, [dateLocked]);

    const getCheckoutCalendarDynamicProps = (currentCheckInDate, normalizedDatesLock, nightsToStay) => {
        if (!(currentCheckInDate instanceof Date && !isNaN(currentCheckInDate))) {
            return {
                minDate: null,
                maxDate: null,
                disabledDates: normalizedDatesLock,
                disabled: true,
            };
        }
    
        const checkInDayOnly = new Date(currentCheckInDate.getFullYear(), currentCheckInDate.getMonth(), currentCheckInDate.getDate());
    
        const calculatedMinDate = addDays(checkInDayOnly, nightsToStay);
    
        let calculatedMaxDate = null;
        const sortedLocksAfterCheckIn = normalizedDatesLock
            .filter(d => d.getTime() > checkInDayOnly.getTime())
            .sort((a, b) => a.getTime() - b.getTime());
    
        if (sortedLocksAfterCheckIn.length > 0) {
            calculatedMaxDate = sortedLocksAfterCheckIn[0];
        }
    
        const specificDisabledDatesInRange = [];
        if (calculatedMinDate && calculatedMaxDate) {
            normalizedDatesLock.forEach(lockedDate => {
                if (lockedDate.getTime() >= calculatedMinDate.getTime() &&
                    lockedDate.getTime() < calculatedMaxDate.getTime()) {
                    specificDisabledDatesInRange.push(lockedDate);
                }
            });
        }
        else if (calculatedMinDate) {
             normalizedDatesLock.forEach(lockedDate => {
                if (lockedDate.getTime() >= calculatedMinDate.getTime()) {
                    specificDisabledDatesInRange.push(lockedDate);
                }
            });
        }
    
    
        return {
            minDate: calculatedMinDate,
            maxDate: calculatedMaxDate, 
            disabledDates: [...new Set(specificDisabledDatesInRange.map(d => d.getTime()))].map(t => new Date(t)),
            disabled: false,
        };
    };
    

    useEffect(() => {
        const props = getCheckoutCalendarDynamicProps(checkInDate, normalizedGlobalDateLocked, minNights);
    
        setCheckoutMinDate(props.minDate);
        setCheckoutMaxDate(props.maxDate);
        setCheckoutDisabledDates(props.disabledDates);
        setIsCheckoutDisabled(props.disabled);
    
        if (checkOutDate) {
            let isCurrentCheckoutValid = true;
            const currentCheckoutDayOnly = new Date(checkOutDate.getFullYear(), checkOutDate.getMonth(), checkOutDate.getDate());
    
            if (props.minDate && currentCheckoutDayOnly.getTime() < props.minDate.getTime()) {
                isCurrentCheckoutValid = false;
            }
            if (props.maxDate && currentCheckoutDayOnly.getTime() > props.maxDate.getTime()) {
                isCurrentCheckoutValid = false;
            }
            if (props.disabledDates.some(d => d.getTime() === currentCheckoutDayOnly.getTime())) {
                isCurrentCheckoutValid = false;
            }
            if (props.disabled) { 
                 isCurrentCheckoutValid = false;
            }
    
            if (!isCurrentCheckoutValid) {
                setCheckOutDate(null);
            }
        }
    }, [checkInDate, normalizedGlobalDateLocked, minNights, checkOutDate]);


    const handleBook = () => {
        if (checkInDate && checkOutDate && guests) {
            if (bookNowButtonRef.current) {
                const rect = bookNowButtonRef.current.getBoundingClientRect();
                setModalPosition({
                    top: rect.top + window.scrollY + rect.height + 10, 
                    left: rect.left + window.scrollX,
                });
            }
            setPaymentModeVisible(true);

            let url = `https://booking.feelingitaly.com/en/properties/${villa.idGuesty}`;
            const params = new URLSearchParams();
            params.append('minOccupancy', guests);
            params.append('checkIn', FormatDate(new Date(checkInDate)));
            params.append('checkOut', FormatDate(new Date(checkOutDate)));

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
            setLink(url);
        }
    }

    const checkMinNights = async (checkIn) => {
        if (villa.idGuesty && checkIn) {
            const resp = await CheckMinNights(villa.idGuesty, checkIn);
            // se effettivamente hanno settato un min nights specifico lo settiamo
            if(minNights !== resp) {
                setMinNights(resp)
            }
        }
    }

    const getAvailability = async (isFirst) => {
        if (villa.idGuesty) {
            const resp = await GetAvailabilityById(villa.idGuesty, checkInDate, checkOutDate);

            if (isFirst && resp && resp.notAvailableDates && resp.notAvailableDates.length > 0) {
                setDateLocked(resp.notAvailableDates.map(x => { return new Date(x) }));
                setMinNights(resp.minNights ?? 1); // lasciamo che venga settato anche all'inizio il min nights generale
                setLatitude(resp.position.lat);
                setLongitude(resp.position.lng);
            } else if (!isFirst) { 
                setPriceNight(Number(resp.pricePerNight).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                setPriceTotal(Number(resp.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                if (resp && resp.pricePerNight && resp.totalPrice) {
                    setIsAvailable(true);
                } else {
                    setIsAvailable(false);
                }
            }
            return resp; 
        }
        return null; 
    }

    useEffect(() => {
        getAvailability(true);
        // eslint-disable-next-line 
    }, []);

    const handleVisibleMasonry = () => {
        setIsModalMasonryOpen(!isModalMasonryOpen);
    }



    useEffect(() => {
    }, [dateLocked, minNights, latitude, longitude]);

    useEffect(() => {
    }, [priceNight, priceTotal, isAvailable]);

    useEffect(() => {
    }, [link, paymentModeVisible]);

    useEffect(() => {
        if (checkInDate && checkOutDate && guests) {
            setCanCheck(true);
        }
    }, [checkInDate, checkOutDate, guests]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line 
    }, [isClosed]);

    const handleScroll = () => {
        const scrollY = window.scrollY;

        if (scrollY > 200) {
            setIsPopUpVisible(false);
        } else if (scrollY < 200 && isClosed) {
            setIsPopUpVisible(false);
        } else {
            setIsPopUpVisible(true);
        }
    };

    return (
        <>
            <Seo
                title={villa?.seoInformation?.title ?? "Property collection | Feeling Italy"}
                description={villa?.seoInformation?.metaDescription ?? `Discover ${villa.name}, a stunning Mediterranean villa offered by Feeling Italy. Enjoy breathtaking views, luxury amenities, and an unforgettable stay on the Amalfi Coast.`}
                keywords={villa?.seoInformation?.keywords ?? `${villa.name}, Luxury Villa Amalfi Coast, Mediterranean Villa Stay, Feeling Italy Property, Exclusive Villa with Sea Views, Premium Italian Accommodation, Vacation Rental Amalfi Coast, Private Villa Amenities, Sorrento Villas, Tailored Villa Experiences`}
                url={window.location.href}
            />
            <Header background={"transparent"} />
            <div 
                className="sectionHeadingPage detail" 
                style={{ 
                    backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.8) 3%, rgba(0, 0, 0, 0) 38%), 
                                    url(${process.env.REACT_APP_SITE_URL + (villa?.gallery && (villa?.gallery[0]?.objectUrl ?? ""))})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat"
                }}
            >
                <div className={"container-fluid text-center w-50"}></div>
                <div className={"d-flex justify-content-center mt-5"}><button className="text-uppercase btn-tot-photos" onClick={handleVisibleMasonry}>{villa.gallery.length > 0 ? villa.gallery.length : ""} {t("Photos")}</button></div>
            </div>
            {isPopUpVisible && !isMobileTablet && (
                <LateralPopUp onClose={(isClosed) => setIsClosed(isClosed)} />
            )}
            {isModalMasonryOpen && (
                <MasonryProperty photoGallery={villa.gallery} onClick={handleVisibleMasonry} />
            )}
            {!isMobileTablet && (
                <div className="background-white w-100 p-3" id="menu-internal">
                    <div className="container-fluid p-0">
                        <div className={`row background-white m-0 w-100 z-1`}>
                            <div className="col-12 text-center">
                                <div className={`d-flex justify-content-center header-internal`}>
                                    <HashLink className="lineEffectBlack text-black me-5 text-uppercase" smooth to="#overview">
                                        {t('Overview')}
                                    </HashLink>
                                    <HashLink className="lineEffectBlack text-black me-5 text-uppercase" smooth to="#in-villa-personal-concierge">
                                        {t('PersonalConcierge')}
                                    </HashLink>
                                    <a 
                                        href=""
                                        className="lineEffectBlack bg-transparent border-none text-black me-5 cursor-pointer text-uppercase"
                                        onClick={(e) => {
                                        e.preventDefault();
                                        handleVisibleMasonry();
                                    }}>
                                        {t('Gallery')}
                                    </a>
                                    <HashLink className="lineEffectBlack text-black me-5 text-uppercase" smooth to="#area">
                                        {t('TheArea')}
                                    </HashLink>
                                    <a 
                                        href=""
                                        className="lineEffectBlack bg-transparent border-none text-black me-5 cursor-pointer text-uppercase" 
                                        onClick={(e) => {
                                        e.preventDefault();
                                        setIsOpenPropertyRules(true);
                                    }}>
                                        {t('PropertyRules')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="container-property mt-5" id="intro">
                <div className="row">
                    <div className="col-lg-8 col-12 pr-6 container-description-villa">
                        <div className={`d-flex ${isMobileTablet ? "justify-content-center flex-column" : ""}`}>
                            {villa.guests && villa.guests > 0 && (
                                <h3 className="sub-title border-end border-2 border-dark text-uppercase pe-5">{villa.guests} {t('Guests')}</h3>
                            )}
                            {villa.bedrooms && villa.bedrooms > 0 && (
                                <h3 className={`sub-title border-end border-2 border-dark text-uppercase pe-5 ${villa.guests > 0 ? 'ps-5' : ''}`}>{villa.bedrooms} {t('Bedrooms')}</h3>
                            )}
                            {villa.beds && villa.beds > 0 && (
                                <h3 className={`sub-title border-end border-2 border-dark text-uppercase pe-5 ${(villa.guests > 0 || villa.bedrooms > 0) ? 'ps-5' : ''}`}>{villa.beds} {t('Beds')}</h3>
                            )}
                            {villa.bathrooms && villa.bathrooms > 0 && (
                                <h3 className={`sub-title text-uppercase ${(villa.guests > 0 || villa.bedrooms > 0 || villa.beds > 0) ? 'ps-5' : ''}`}>{villa.bathrooms} {t('Bathrooms')}</h3>
                            )}
                        </div>
                        <div className="pt-5 text-left pb-3 ">
                            <p className="paragraph mb-3" dangerouslySetInnerHTML={{ __html: villa?.intro ?? "" }} />
                        </div>
                        {villa.brochure && (
                            <div className="">
                                <a className={"button-villa-brochure text-capitalize text-nowrap"} download={(villa?.brochure[0]?.name ?? "")} href={process.env.REACT_APP_SITE_URL + (villa.brochure && (villa?.brochure[0]?.objectUrl ?? ""))} >{t("DownloadBrochure")} </a>
                            </div>
                        )
                        }
                    </div>
                    <div className="col-lg-4 col-12 centered-card-detail-villa d-flex justify-content-center">
                        <div className="card-detail-villa px-0">
                            <div className="header-card background-black text-white pt-1 pb-1">
                                <h4 className="text-center p-3 text-uppercase title">{villa.name}</h4>
                            </div>
                            <div className="py-3 px-3">
                                <div className="row mt-3">
                                    <div className="col-6 select-no-outline mt-1 d-flex justify-content-center flex-column">
                                        <label className="text-uppercase fw-semibold">{t('Checkin')}</label>
                                        <Calendar
                                            value={checkInDate}
                                            disabled={!dateLocked ? true : false}
                                            onChange={async(e) => {
                                                setCheckInDate(e.value);
                                                setCheckOutDate(null);
                                                setIsAvailable(false);
                                                await checkMinNights(e.value);
                                            }}
                                            inputClassName="w-full my-0 custom-select pb-0 input-calendar-custom"
                                            className="w-full custom-select mb-4 mb-lg-0 my-2"
                                            placeholder={t('dd/mm/yy')}
                                            dateFormat="dd/mm/yy"
                                            selectionMode="single"
                                            minDate={addDays(new Date(Date.now()), 1)}
                                            disabledDates={dateLocked}
                                            readOnlyInput={true}
                                            viewDate={checkInDate ? checkInDate : new Date(Date.now())}
                                        />
                                    </div>
                                    <div className="col-6 select-no-outline mt-1 d-flex justify-content-center flex-column">
                                        <label className="text-uppercase fw-semibold">{t('Checkout')}</label>
                                        <Calendar
                                            value={checkOutDate}
                                            onChange={(e) => {
                                                setCheckOutDate(e.value);
                                                setIsAvailable(false);
                                            }}
                                            inputClassName="w-full my-0 custom-select pb-0 input-calendar-custom"
                                            className="w-full custom-select mb-4 mb-lg-0 my-2"
                                            placeholder={t('dd/mm/yy')}
                                            dateFormat="dd/mm/yy"
                                            selectionMode="single"
                                            minDate={checkoutMinDate}
                                            maxDate={checkoutMaxDate}
                                            disabledDates={checkoutDisabledDates} 
                                            readOnlyInput={true}
                                            viewDate={checkOutDate ? checkOutDate : checkoutMinDate ? checkoutMinDate : (checkInDate ? addDays(checkInDate, minNights) : null) }
                                            disabled={isCheckoutDisabled}
                                        />
                                    </div>
                                </div>
                                <div className="select-no-outline mt-3">
                                    <label className="mb-2 text-uppercase fw-semibold">{t('NumberGuests')}</label>
                                    <Select
                                        className="w-full no-radius"
                                        options={guestOptions}
                                        value={guestOptions && guests ? guestOptions.find((x) => x.value === guests) : null}
                                        onChange={(e) => setGuests(e.value)}
                                        placeholder={t('AddGuest')}
                                        isSearchable={false}
                                    />
                                </div>
                                {/* {isAvailable ? (
                                    <>
                                        <div className="justify-content-between d-flex mt-3">
                                            <p className="grey-dark fs-14">€{priceNight}/{t("Night")}</p>
                                            <p className="fs-14 fw-semibold">€{priceTotal}</p>
                                        </div>
                                        <div className="justity-content-center d-flex flex-column mb-2 mt-2">
                                            <button onClick={((e) => {
                                                e.preventDefault();
                                                handleBook();
                                            })} ref={bookNowButtonRef} 
                                            className="text-white background-black py-2 px-2 me-1 text-center border-custom text-uppercase">
                                                {t('BookNow')}
                                            </button>
                                            <div className="justify-content-center d-flex align-items-center mb-2 mt-2">
                                                <div className="line-separator me-2" />{t("Or")}<div className="line-separator ms-2" />
                                            </div>
                                            <HashLink to={"#contact-travel-designer"} smooth className="bg-transparent border-custom text-black text-center ms-1 text-decoration-none py-2 px-2">
                                                {t("ContactOurTravelDesigner")}
                                            </HashLink>
                                        </div>
                                    </>
                                ) : (
                                    <div className="justity-content-center d-flex flex-column mb-5 mt-5">
                                        <button disabled={!canCheck} onClick={((e) => {
                                            e.preventDefault();
                                            getAvailability(false);
                                        })} className="text-white background-black py-2 px-2 me-1 text-center border-custom text-uppercase">
                                            {t('CheckAvailability')}
                                        </button>
                                        <div className="justify-content-center d-flex align-items-center mb-2 mt-2">
                                            <div className="line-separator me-2" />{t("Or")}<div className="line-separator ms-2" />
                                        </div>
                                        <HashLink to={"#contact-travel-designer"} smooth className="bg-transparent border-custom text-black text-center ms-1 text-decoration-none py-2 px-2">
                                            {t("TalkWithUs")}
                                        </HashLink>
                                    </div>
                                )} */}

                                <div className="justity-content-center d-flex flex-column mb-5 mt-5">
                                    <button 
                                        disabled={!canCheck} 
                                        onClick={(async(e) => {
                                            e.preventDefault();
                                            await getAvailability(false).then((resp) => {
                                                if(resp) {
                                                    handleBook();
                                                }
                                            });
                                        })} 
                                        className="text-white background-black py-2 px-2 me-1 text-center border-custom text-uppercase">
                                            {t('CheckAvailability')}
                                    </button>
                                    <div className="justify-content-center d-flex align-items-center mb-2 mt-2">
                                        <div className="line-separator me-2" />{t("Or")}<div className="line-separator ms-2" />
                                    </div>
                                    <HashLink to={"#contact-travel-designer"} smooth className="bg-transparent border-custom text-black text-center ms-1 text-decoration-none py-2 px-2">
                                        {t("TalkWithUs")}
                                    </HashLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <section className="container-property" id="overview" ref={overviewRef}>
                <Accordion defaultActiveKey="0" className="mt-5 accordion-detail">
                    {accordionItems.map((item, index) => {
                        if (item?.description) {
                            const descriptionWithoutOl =
                                typeof item.description === "string"
                                    ? item.description.replace(/<\/?ol>/g, "")
                                    : item.description;
                            return (
                                <Accordion.Item eventKey={index.toString()} key={item.id} className="mb-5" id={item.id}>
                                    <Accordion.Header>{t(item.title).toUpperCase()}</Accordion.Header>
                                    <Accordion.Body className="pe-0">
                                        {item.id === "amenities" && Array.isArray(item.description) ? (
                                            <ul className="d-flex flex-wrap ps-4">
                                                {item.description.map((a, i) => (
                                                    <li key={i} className={`${isMobileTablet ? "w-50" : "w-25"} mt-1`}>{t(a)}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="paragraph m-0" dangerouslySetInnerHTML={{ __html: descriptionWithoutOl }} />
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        }
                        return null;
                    })}
                </Accordion>
            </section>
            <section id="in-villa-personal-concierge">
                <CardsConcierge cards={CardsExperiences.filter(x => x.typology === villa.typology)} />
            </section>
            <section className="container-property" id="area">
                <h5 className="title text-uppercase fs-18 pb-3 border-bottom border-black">{t("TheArea")}</h5>
                <p className="pt-1" dangerouslySetInnerHTML={{ __html: villa.theArea ?? "" }}/>
                <div className="mb-5 pb-5 pt-4">
                    {latitude > 0 && longitude > 0 && (
                        <APIProvider apiKey='AIzaSyAsAdPEMzcmxKyTpSGnoaEJWGDplIt6lNI'>
                            <Map className="map-style"
                                defaultCenter={{ lat: latitude, lng: longitude }}
                                defaultZoom={13}
                                mapId={'897ed54acb1cb29e'}>
                                <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
                                    <IoHomeOutline className="icon-map" />
                                </AdvancedMarker>
                            </Map>
                        </APIProvider>
                    )}
                </div>
            </section>
            {isMobileTablet &&
                <section className="container-property" id="rules">
                    <h5 className="title text-uppercase fs-18 pb-3 border-bottom border-black">{t("PropertyRules")}</h5>
                    <p className="pt-1">{t("ReadRulesDescription")} <span className="text-decoration-underline" onClick={(e) => {
                        e.preventDefault();
                        setIsOpenPropertyRules(true);
                    }}>{t("Here")}</span>.</p>
                </section>
            }
            <>
                <div ref={finalRef}></div>
                {/* CONTACT */}
                <ContactConcierge margin="-40px" />
            </>
            {!isMobileTablet && (
                <section className="mb-3">
                    <div className={`background-black text-white container-search-bar w-100 pt-3 ${isBookingVisible ? "booking-engine-container" : ""} pb-3`} id="black-booking-engine">
                        <div className="row">
                            <div className="col-lg-3 col-12 select-no-outline border-white-left d-flex justify-content-center flex-column pr-6">
                                <label className="mb-2 text-uppercase">{t('Checkin')}</label>
                                <Calendar
                                    value={checkInDate}
                                    disabled={!dateLocked ? true : false}
                                    onChange={async(e) => {
                                        setCheckInDate(e.value);
                                        setCheckOutDate(null);
                                        setIsAvailable(false);
                                        await checkMinNights(e.value);
                                    }}
                                    inputClassName="w-full  custom-select pb-2 input-calendar-custom-black"
                                    className="w-full custom-select"
                                    placeholder={t('SelectCheckIn')}
                                    dateFormat="dd/mm/yy"
                                    selectionMode="single"
                                    minDate={addDays(new Date(Date.now()), 1)}
                                    disabledDates={dateLocked}
                                    readOnlyInput={true}
                                    viewDate={checkInDate ? checkInDate : new Date(Date.now())}
                                />
                            </div>
                            <div className="col-lg-3 col-12 select-no-outline border-white-left d-flex justify-content-center flex-column px-6">
                                <label className="mb-2 text-uppercase">{t('Checkout')}</label>
                                <Calendar
                                    value={checkOutDate}
                                    onChange={(e) => {
                                        setCheckOutDate(e.value);
                                        setIsAvailable(false);
                                    }}
                                    inputClassName="w-full background-black custom-select pb-2 input-calendar-custom-black"
                                    className="w-full background-black custom-select"
                                    placeholder={t('SelectCheckOut')}
                                    dateFormat="dd/mm/yy"
                                    selectionMode="single"
                                    minDate={checkoutMinDate}
                                    maxDate={checkoutMaxDate}
                                    disabledDates={checkoutDisabledDates} 
                                    readOnlyInput={true}
                                    viewDate={checkOutDate ? checkOutDate : checkoutMinDate ? checkoutMinDate : (checkInDate ? addDays(checkInDate, minNights) : null) }
                                    disabled={isCheckoutDisabled}
                                />
                            </div>
                            <div className="col-lg-3 col-12 select-no-outline d-flex justify-content-center flex-column pl-6">
                                <label className="mb-2 text-uppercase">{t('AddGuest')}</label>
                                <Select
                                    className="w-full bg-transparent text-white input-calendar-custom-black-guest custom-select"
                                    options={guestOptions}
                                    value={guests ? guestOptions.find((x) => x.value === guests.value) : null}
                                    onChange={(e) => setGuests(e.value)}
                                    placeholder={t('SelectNumberOfGuests')}
                                    isSearchable={false}
                                    menuPlacement="top"
                                />
                            </div>
                            <div className="col-lg-3 col-12 d-flex justify-content-center h-25">
                                <button disabled={!canCheck}
                                    onClick={async(e) => {
                                        e.preventDefault();
                                        await getAvailability(false);
                                        document.getElementById("intro")?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className="background-white text-black py-3 px-4 mt-3 w-full text-left border-none text-uppercase">
                                    {t('CheckAvailability')}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}
                <Modal
                    open={paymentModeVisible}
                    className="custom-modal-property"
                    onClose={() => setPaymentModeVisible(!paymentModeVisible)}
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description"
                >
                    <Box
                        sx={{
                            position: "fixed",
                            top: "50%",       
                            left: "50%",     
                            transform: "translate(-50%, -50%)", 
                            width: !isMobileTablet ? "30vw" : "84vw", 
                            height: !isMobileTablet ? "70vh" : "88vh",
                            zIndex: "999999",
                            bgcolor: "#FFFFFF",
                            border: "none",
                            borderBlockWidth: "0",
                            boxShadow: "2px 10px 10px 6px rgba(0, 0, 0, 0.2)",
                            p: 0,
                            margin: 'auto', 
                            outline: 'none', 
                        }}
                    >
                    <button
                        className="back-experience-cta mt-2 mb-2 background-white text-black px-4 text-end w-100 border-none text-uppercase effectArrowLeft link"
                        onClick={() => setPaymentModeVisible(!paymentModeVisible)}
                    >
                        <FontAwesomeIcon icon={faTimes} className="mt-1" color="black" fontSize={"16px"} />
                    </button>
                    <iframe title={"Process payment"} src={link} width={"100%"} height={"90%"} style={{padding : "3%"}}></iframe>
                </Box>
            </Modal>
            <Modal
                open={isOpenPropertyRules}
                className="custom-modal-property"
                onClose={() => setIsOpenPropertyRules(!isOpenPropertyRules)}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: "fixed",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100vh",
                        zIndex: "999999",
                        bgcolor: "#FFFFFF",
                        border: "none",
                        borderBlockWidth: "0",
                        boxShadow: 0,
                        p: 0,
                    }}
                >
                    <div className="arrowEffectContainer">
                        <button
                            className="back-experience-cta ps-4 ms-lg-5 ps-lg-5 mt-5 background-white text-black px-lg-4 w-lg-50 text-start border-none text-uppercase link"
                            onClick={() => setIsOpenPropertyRules(!isOpenPropertyRules)}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2 paddingArrow effectArrowLeft" />
                            {t("OverviewVilla")}
                        </button>
                    </div>
                    <PropertyRules headerInvisible={true} noClickableMenu="true" />
                </Box>
            </Modal>
        </>
    );
};

export default TemplatePropertyDetail;